import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { InitialDataService } from '@services/loadInitialData';
import { PermissionsService } from '@services/permissions';
import { surveyRenderDataFactory } from '@services/surveyRenderData';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { hasItems } from '@utils/vanillaJs';
import {
  Collaborator,
  Invitation,
  InvitationEnvelope,
  Member,
  UserGroup
} from '@vincerosoft/assessfluence-types';
import { limit, QueryConstraint, QueryFieldFilterConstraint, where } from 'firebase/firestore';

function invitationsFactory() {
  const loadInvitationsStore = async (
    member: Member,
    userGroups: UserGroup[],
    onProgress: (invitations: InvitationEnvelope[]) => void
  ) => {
    const { query, batchQuery } = getQuery(member, userGroups);
    query.push(where('linked_campaign.trigger', '==', 'campaigns'));

    let invitations: InvitationEnvelope[] = [];
    await InitialDataService.load<InvitationEnvelope>(
      'invitations',
      query,
      {},
      (data) => {
        invitations = data;
        onProgress(data);
      },
      batchQuery
    );

    const referenceData = await getReferenceData(invitations);
    return { ...referenceData, invitations };
  };

  const loadCollaboratorInvitations = async (memberId: string) => {
    const inFlightStore: {
      collaborators: Collaborator[];
      invitations: InvitationEnvelope[];
    } = {
      collaborators: [],
      invitations: []
    };
    // Fetch collaborator records for this member (where member_id == memberId)
    const collaborators = await collectionsService.collaborators.getDocs(
      where('member_id', '==', memberId)
    );

    // Persist collaborator records into the global invitations store
    inFlightStore.collaborators = collaborators;

    // If the member has no collaborator records, ensure invitations is an empty array and exit
    if (!hasItems(collaborators)) {
      return inFlightStore;
    }

    // Fetch invitation envelopes referenced by the collaborator records in batches.

    const invEnvelopes = await collectionsService.invitations.getDocsInBatches([
      'id',
      'in',
      collaborators.map((collaboration) => collaboration.invitation_id)
    ]);

    // Flatten batches and filter each envelope's invitations to only those the current member collaborates on.
    const invitations = invEnvelopes.flat().map((inv) => {
      const invEnv = inv as InvitationEnvelope;

      // Keep only the nested invitations where one of the collaborator ids matches our collaborator records
      invEnv.invitations = invEnv.invitations.filter((invitation) => {
        const coll = collaborators.find(({ id }) => {
          return invitation.collaborator_ids?.includes(id);
        });

        return !!coll;
      });

      return invEnv;
    });

    // Save the filtered collaborator-related invitation envelopes into the global store
    inFlightStore.invitations = invitations;

    return inFlightStore;
  };

  const getQuery = (member: Member, userGroups: UserGroup[]) => {
    if (PermissionsService.canReadAll('invitation')) {
      return {
        query: [where('entity_id', '==', member.entity_id)]
      };
    }

    if (hasItems(member.user_group_ids) && PermissionsService.canReadGroup('invitation')) {
      if (!hasItems(userGroups)) {
        return { query: [where('member_id', '==', member.id)] };
      }

      const memberIds = userGroups.reduce<string[]>(
        (acc, userGroup) => acc.concat(...userGroup.member_ids),
        []
      );

      if (memberIds.length === 0) {
        return { query: [where('member_id', '==', member.id)] };
      }

      const batchQuery = ['member_id', 'in', memberIds.concat(member.id)] as [
        string,
        'in' | 'array-contains-any',
        string[]
      ];

      return {
        query: [] as unknown as QueryConstraint[],
        batchQuery
      };
    }

    return { query: [where('member_id', '==', member.id)] };
  };

  const getReferenceData = async (invitations: InvitationEnvelope[]) => {
    const linkedCampaignIds = invitations
      .map((inv) => inv.linked_campaign?.id)
      .filter((id): id is string => !!id);
    return surveyRenderDataFactory(invitations, 'invitation', linkedCampaignIds);
  };

  const getInvitationById = (invitationId: string) => {
    return collectionsService.invitations.getDoc(invitationId);
  };

  const getInvitationsByIds = async (
    invitationIds: string[],
    progressCallback?: (progress: InvitationEnvelope[]) => void
  ) => {
    const invitationsInBatches = await collectionsService.invitations.getDocsInBatches(
      ['id', 'in', invitationIds],
      undefined,
      progressCallback
    );
    return invitationsInBatches.flat();
  };

  const getInvitations = async (
    userGroups: UserGroup[],
    options?: { fromDate?: Date; limit?: number }
  ) => {
    const member = appUserStore.getState().currentMember!;
    // eslint-disable-next-line prefer-const
    let { query, batchQuery } = getQuery(member, userGroups);
    query = query || [];
    if (options?.fromDate) {
      query.push(where('created_at', '>=', options.fromDate));
    }
    if (options?.limit) {
      query.push(limit(options.limit) as unknown as QueryFieldFilterConstraint);
    }
    if (batchQuery) {
      const invitationsInBatches = await collectionsService.invitations.getDocsInBatches<
        InvitationEnvelope & { created_at: Date }
      >(batchQuery, query);
      return invitationsInBatches.flat();
    }
    return collectionsService.invitations.getDocs<InvitationEnvelope & { created_at: Date }>(query);
  };

  const getInvitationCounts = async (member: Member, userGroups: UserGroup[]) => {
    // eslint-disable-next-line prefer-const
    let { query, batchQuery } = getQuery(member, userGroups);
    query = query || [];
    if (batchQuery) {
      return collectionsService.invitations.getCountInBatches(batchQuery, query);
    }
    const invitations = await collectionsService.invitations.getDocs(query);
    return invitations.length;
  };

  function getInvitationsByCampaignId(campaignId: string) {
    return collectionsService.invitations.getDocs(where('linked_campaign.id', '==', campaignId));
  }

  async function getInvitationsByCampaignIds(campaignIds: string[]) {
    const invitationsInBatches = await collectionsService.invitations.getDocsInBatches([
      'linked_campaign.id',
      'in',
      campaignIds
    ]);
    return invitationsInBatches.flat();
  }

  async function getInvitationsByMemberId(memberId: string) {
    return collectionsService.invitations.getDocs(where('member_id', '==', memberId));
  }

  function matchesFormId(formId: string) {
    return (inv: Invitation) =>
      inv &&
      (inv.form_id === formId ||
        (inv as Invitation & { linked_form: { id: string } }).linked_form?.id === formId);
  }

  return {
    loadInvitationsStore,
    loadCollaboratorInvitations,
    getQuery,
    getInvitations,
    getInvitationsByIds,
    getInvitationCounts,
    getInvitationsByCampaignId,
    getReferenceData,
    getInvitationById,
    getInvitationsByCampaignIds,
    getInvitationsByMemberId,
    matchesFormId
  };
}

export const invitationsService = invitationsFactory();
